import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Store, ArrowRight, CheckCircle2, Clock as ClockIcon } from 'lucide-react';

/**
 * Contextual CTA shown on the Member Dashboard for the merchant-application
 * flow. Three states — not-applied (apply CTA), pending, approved (switch
 * to Merchant Mode). Only renders for active-subscriber members.
 */
export function MerchantPartnerStatusBanner({ user, api }) {
  const [app, setApp] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    if (user?.role !== 'member' || user?.subscription_status !== 'active') {
      setLoaded(true);
      return;
    }
    api.get('/api/merchant/application/me')
      .then((r) => { if (alive) setApp(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [api, user?.role, user?.subscription_status]);

  if (!loaded || user?.role !== 'member' || user?.subscription_status !== 'active') return null;

  const status = app?.merchant_status;
  const isApproved = !!app?.is_merchant_partner;

  if (isApproved) {
    return (
      <div
        className="mb-6 flex items-start gap-3 rounded-xl border border-[#00E676]/30 bg-gradient-to-r from-[#00E676]/10 to-transparent p-4"
        data-testid="merchant-partner-approved-banner"
      >
        <CheckCircle2 className="w-5 h-5 text-[#00E676] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-white font-medium">You&apos;re a verified merchant partner</p>
          <p className="text-sm text-[#A0B4CC] mt-0.5">Switch to Merchant Mode from the sidebar to manage your services and view discount claims.</p>
        </div>
        <button
          className="text-xs px-3 py-1.5 rounded-lg bg-[#00E676] text-[#001627] hover:bg-[#00C853] font-medium whitespace-nowrap inline-flex items-center gap-1"
          data-testid="banner-switch-to-merchant"
          onClick={async () => {
            try {
              await api.post('/api/users/switch-view', { view: 'merchant' });
              window.location.href = '/merchant';
            } catch {/* noop */}
          }}
        >
          Merchant Mode <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#FFC107]/30 bg-gradient-to-r from-[#FFC107]/10 to-transparent p-4" data-testid="merchant-partner-pending-banner">
        <ClockIcon className="w-5 h-5 text-[#FFC107] mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-white font-medium">Merchant application under review</p>
          <p className="text-sm text-[#A0B4CC] mt-0.5">We&apos;ll email you when there&apos;s an update on &ldquo;{app?.merchant_info?.business_name}&rdquo;.</p>
        </div>
      </div>
    );
  }

  // Not applied (or rejected) — show the CTA.
  return (
    <Link
      to="/merchant/apply"
      className="mb-6 flex items-start gap-3 rounded-xl border border-[#00E5FF]/25 bg-gradient-to-r from-[#00E5FF]/10 to-transparent p-4 hover:border-[#00E5FF]/60 transition-colors"
      data-testid="merchant-partner-apply-cta"
    >
      <Store className="w-5 h-5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-white font-medium">{status === 'rejected' ? 'Reapply as a merchant partner' : 'Become a Merchant Partner'}</p>
        <p className="text-sm text-[#A0B4CC] mt-0.5">
          {status === 'rejected'
            ? 'Update your business details and submit again.'
            : 'Offer your services to AGA members. Switch between Member and Merchant Mode anytime after approval.'}
        </p>
      </div>
      <span className="text-[#00E5FF] text-xs inline-flex items-center gap-1 whitespace-nowrap">Apply <ArrowRight className="w-3.5 h-3.5" /></span>
    </Link>
  );
}
